import {
  Badge,
  Box,
  CellProps,
  Flex,
  Text,
  Tooltip,
  usePagination,
  useTable,
} from '@agrotoken-tech/ui';
import {
  CREDITOR_PLEDGE_STATUSES_ICON,
  creditorCropPledgeStatusBadgeColors,
} from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { ExplorerCropPledgeListCreditorSerializer } from '@bcba/serializers';
import {
  CROP_PLEDGE_USER_ROLES,
  PLEDGE_STATUSES,
  PledgeStatus,
  pledgeStatuses,
} from '@bcba/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { getCreditorCropPledge } from '../../../services/crop-pledge';
import { useTableFilters } from '../../../utils/useTableFilters';

export const useCreditorCropPledge = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const {
    formMethods,
    debouncedSearch,
    debouncedStatuses,
    selectedStatuses,
    setSelectedStatuses,
    onFiltersReset,
  } = useTableFilters<PledgeStatus>();

  const { data, isLoading } = useQuery({
    queryKey: [
      'get-creditor-crop-pledge',
      page,
      debouncedSearch,
      debouncedStatuses,
    ],
    queryFn: () =>
      getCreditorCropPledge({
        page,
        search: debouncedSearch,
        statuses: debouncedStatuses,
      }),
  });

  const columns = useMemo(
    () => [
      {
        Header: t('cropPledge.table.column.internalIdentifier'),
        accessor: 'internalIdentifier',
        Cell: (props: CellProps<ExplorerCropPledgeListCreditorSerializer>) => {
          const { internalIdentifier } = props.row.original;
          return <Text># {internalIdentifier}</Text>;
        },
      },
      {
        Header: t('cropPledge.table.column.companyName'),
        accessor: 'companyName',
        Cell: (props: CellProps<ExplorerCropPledgeListCreditorSerializer>) => (
          <Text
            size={'sm'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            maxW={'200px'}
          >
            {props.row.original.companyName}
          </Text>
        ),
      },
      {
        Header: t('cropPledge.table.column.fiscalId'),
        accessor: 'companyFiscalId',
      },
      {
        Header: t('cropPledge.table.column.status'),
        accessor: 'status',
        Cell: (props: CellProps<ExplorerCropPledgeListCreditorSerializer>) => {
          const { statuses = [] } = props.row.original;
          return (
            <Flex gap={0.5}>
              {statuses.map((status) => (
                <Tooltip
                  hasArrow
                  label={t(
                    `cropPledge.status.${CROP_PLEDGE_USER_ROLES.CREDITOR}.${status}.tooltip`,
                  )}
                  title={t(
                    `cropPledge.status.${CROP_PLEDGE_USER_ROLES.CREDITOR}.${status}.tooltip`,
                  )}
                  placement="bottom"
                  bg="white"
                  color="content-secondary"
                  size="sm"
                  minW={'auto'}
                >
                  <Box display="flex" alignItems="center">
                    <Badge
                      colorScheme={creditorCropPledgeStatusBadgeColors[status]}
                      icon={CREDITOR_PLEDGE_STATUSES_ICON[status] || null}
                      iconPosition="leading"
                    >
                      {t(`cropPledge.status.${status}`)}
                    </Badge>
                  </Box>
                </Tooltip>
              ))}
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tableData = React.useMemo(
    () => data?.data || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  const tableInstance = useTable(
    {
      columns: columns as any,
      data: tableData,
    },
    usePagination,
  );

  const statusOptions = pledgeStatuses
    .filter(
      (s) =>
        s !== PLEDGE_STATUSES.PENDING_DEBTOR_LOAD &&
        s !== PLEDGE_STATUSES.PENDING_BOLSA_APPROVAL,
    )
    .map((s) => ({
      text: t(`cropPledge.status.${s}`),
      value: s,
    }));

  useEffect(() => setPage(1), [debouncedSearch, debouncedStatuses]);

  useEffect(() => {
    return () => {
      queryClient.cancelQueries();
    };
  }, []);

  return {
    formMethods,
    tableInstance,
    isLoading,
    data,
    setPage,
    statusOptions,
    selectedStatuses,
    setSelectedStatuses,
    onFiltersReset,
  };
};

export default useCreditorCropPledge;
